import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import type { WeeklySummary } from '../types/api.ts';

const weeklyKey = (iso: string) => ['weeklySummary', iso] as const;

/**
 * Fetch (live stats + cached LLM summary) for one Mon-anchored week.
 *
 * Pass null to disable the query — used when the week has no rides so
 * the calendar can skip the network round-trip entirely.
 */
export function useWeeklySummary(iso: string | null) {
  return useQuery({
    queryKey: weeklyKey(iso ?? ''),
    queryFn: () => {
      if (!iso) throw new Error('useWeeklySummary called with null iso');
      return api.weekly.get(iso);
    },
    enabled: !!iso,
    staleTime: 60_000,
  });
}

/**
 * Generate the LLM summary for one week and write it back into the
 * weeklySummary cache so any rendering subscribers update immediately.
 */
export function useGenerateWeeklySummary() {
  const { t } = useTranslation();
  const addToast = useStore((s) => s.addToast);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (iso: string) => api.weekly.generate(iso),
    onSuccess: (data: WeeklySummary, iso) => {
      qc.setQueryData(weeklyKey(iso), data);
    },
    onError: () => {
      addToast({ message: t('week.errorGenerate'), type: 'error' });
    },
  });
}
